"use client"

import { useState, useEffect, Suspense } from "react"
import { useRouter } from "next/navigation"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Check, Star, Loader2 } from "lucide-react"
import { PricingToggle } from "./PricingToggle"
import { CreditPacksSection } from "./CreditPacksSection"
import { FAQSection } from "./FAQSection"
import { useSession } from "@/app/context/AuthContext"
import { BusinessWaitlistModal } from "./BusinessWaitlistModal"
import { listSubscriptionPlanCatalog, startCheckout, getAggregatedBilling } from "@/src/utils/billingApi"
import { useToast } from "@/hooks/use-toast"
import { hasActiveSubscription } from "@/src/utils/pricing-helpers"

interface PlanData {
  id: string
  name: string
  price: number
  originalPrice?: number
  description: string
  features: string[]
  buttonText: string
  buttonVariant: "default" | "outline"
  popular?: boolean
  sortPrice?: number
}

function PricingPageContent() {
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("monthly")
  const [showWaitlistModal, setShowWaitlistModal] = useState(false)
  const [plans, setPlans] = useState<PlanData[]>([])
  const [loading, setLoading] = useState(true)
  const [hasAnnualPlans, setHasAnnualPlans] = useState(false)
  const [purchaseLoading, setPurchaseLoading] = useState<string | null>(null)
  const [hasMonthlySubscription, setHasMonthlySubscription] = useState(false)
  const [subscriptionLoading, setSubscriptionLoading] = useState(true)
  const router = useRouter()
  const { data: session, status } = useSession()
  const { toast } = useToast()

  const isLoggedIn = status === "authenticated"

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const checkoutParam = urlParams.get("checkout")

    console.log("[v0] PricingPage - URL params:", {
      checkout: checkoutParam,
      type: urlParams.get("type"),
      plan: urlParams.get("plan"),
      allParams: Object.fromEntries(urlParams.entries()),
    })

    if (checkoutParam === "mock") {
      const type = urlParams.get("type")
      const plan = urlParams.get("plan")
      const pack = urlParams.get("pack")
      const credits = urlParams.get("credits")
      const quantity = urlParams.get("quantity")

      const redirectParams = new URLSearchParams({ purchase: "success" })

      if (type) redirectParams.set("type", type)
      if (plan) redirectParams.set("plan", plan)
      if (pack) redirectParams.set("pack", pack)
      if (credits) redirectParams.set("credits", credits)
      if (quantity) redirectParams.set("quantity", quantity)

      router.push(`/confirmation/thank-you?${redirectParams.toString()}`)
    } else if (checkoutParam === "success") {
      const handleLiveSuccess = async () => {
        try {
          if (status === "loading") {
            console.log("[v0] PricingPage - Session still loading, waiting...")
            return
          }

          const checkoutType = sessionStorage.getItem("checkout_type")
          console.log("[v0] PricingPage - Checkout type from session storage:", checkoutType)

          if (checkoutType !== "subscription") {
            console.log("[v0] PricingPage - Not a subscription checkout, skipping")
            return
          }

          sessionStorage.removeItem("checkout_type")

          if (!session?.user?.id) {
            console.log("[v0] PricingPage - No user session, using fallback")
            router.push(`/confirmation/thank-you?purchase=success&type=subscription&plan=subscription`)
            return
          }

          console.log("[v0] PricingPage - Fetching subscription data for user:", session.user.id)

          const billingData = await getAggregatedBilling(session.user.id)

          console.log("[v0] PricingPage - Billing data received:", billingData)

          if (billingData.subscriptions && billingData.subscriptions.length > 0) {
            const currentSub = billingData.subscriptions[0]
            const planKey = currentSub.planKey || "subscription"

            console.log("[v0] PricingPage - Redirecting with plan:", planKey)
            router.push(`/confirmation/thank-you?purchase=success&type=subscription&plan=${planKey}`)
          } else {
            console.log("[v0] PricingPage - No subscription data, using fallback")
            router.push(`/confirmation/thank-you?purchase=success&type=subscription&plan=subscription`)
          }
        } catch (error) {
          console.error("[v0] PricingPage - Failed to fetch subscription details:", error)
          router.push(`/confirmation/thank-you?purchase=success&type=subscription&plan=subscription`)
        }
      }

      handleLiveSuccess()
    }
  }, [router, session?.user?.id, status])

  useEffect(() => {
    const loadPlans = async () => {
      try {
        setLoading(true)

        const catalogResult = await listSubscriptionPlanCatalog()

        const dynamicPlans: PlanData[] = []
        let foundAnnualPlans = false

        if (catalogResult.plans && Array.isArray(catalogResult.plans) && catalogResult.plans.length > 0) {
          catalogResult.plans.forEach((plan) => {
            const monthlyPrice = plan.recurring_amount_cents ? plan.recurring_amount_cents / 100 : 0
            const annualPrice = billingPeriod === "annual" && monthlyPrice > 0 ? monthlyPrice * 0.8 : monthlyPrice

            if (plan.annual_amount_cents || plan.annual_price_cents) {
              foundAnnualPlans = true
            }

            const planData = {
              id: plan.planKey,
              name: plan.marketing?.displayName || plan.planKey,
              price: billingPeriod === "monthly" ? monthlyPrice : annualPrice,
              originalPrice: billingPeriod === "annual" && monthlyPrice > 0 ? monthlyPrice : undefined,
              description: plan.marketing?.badgeText
                  ? `${plan.marketing.badgeText} plan`
                  : `Perfect for ${plan.planKey} users`,
              features: plan.marketing?.featureBullets || [`${plan.monthlyQuota || 0} design credits/month`],
              buttonText:
                  plan.planKey === "starter" ? "Get Started" : `Choose ${plan.marketing?.displayName || plan.planKey}`,
              buttonVariant: plan.planKey === "pro" ? "default" : "outline",
              popular: plan.planKey === "pro",
            }

            dynamicPlans.push(planData)
          })
        }

        setHasAnnualPlans(foundAnnualPlans)

        dynamicPlans.push({
          id: "business",
          name: "Business",
          price: 0,
          originalPrice: undefined,
          description: "For designers & real estate pros",
          features: ["Unlimited design credits", "API access", "Priority support"],
          buttonText: "Join the Waitlist",
          buttonVariant: "outline",
          sortPrice: 999999,
        })

        dynamicPlans.sort((a, b) => {
          const aPrice = (a as any).sortPrice || a.price
          const bPrice = (b as any).sortPrice || b.price
          return aPrice - bPrice
        })

        setPlans(dynamicPlans)
      } catch (error) {
        console.error("Failed to load pricing plans:", error)
        toast({
          title: "Error loading plans",
          description: "Failed to load subscription plans. Please refresh the page.",
          variant: "destructive",
        })
      } finally {
        setLoading(false)
      }
    }

    loadPlans()
  }, [billingPeriod, toast])

  useEffect(() => {
    const checkSubscription = async () => {
      if (!isLoggedIn || !session?.user?.id) {
        setHasMonthlySubscription(false)
        setSubscriptionLoading(false)
        return
      }

      try {
        const billingData = await getAggregatedBilling(session.user.id)

        const hasActiveSub = billingData.subscriptions?.some((sub: any) => hasActiveSubscription(sub)) ?? false
        setHasMonthlySubscription(hasActiveSub)
      } catch (error) {
        console.error("[v0] Failed to fetch subscription status:", error)
        setHasMonthlySubscription(false)
      } finally {
        setSubscriptionLoading(false)
      }
    }

    checkSubscription()
  }, [isLoggedIn, session?.user?.id])

  const handlePlanClick = async (planId: string) => {
    if (!isLoggedIn) {
      if (planId === "business") {
        setShowWaitlistModal(true)
        return
      }
      router.push(`/login?redirect=/pricing`)
      return
    }

    if (planId === "business") {
      setShowWaitlistModal(true)
    } else {
      if (!session?.user?.id) {
        toast({
          title: "Authentication required",
          description: "Please log in to purchase a subscription.",
          variant: "destructive",
        })
        router.push("/login")
        return
      }

      try {
        setPurchaseLoading(planId)
        sessionStorage.setItem("checkout_type", "subscription")
        await startCheckout(planId, session.user.id)
        setTimeout(() => {
          if (purchaseLoading === planId) {
            toast({
              title: "Checkout delayed",
              description: "If you weren't redirected to checkout, please try again.",
              variant: "destructive",
            })
            setPurchaseLoading(null)
          }
        }, 3000)
      } catch (error) {
        console.error("Checkout error:", error)
        toast({
          title: "Checkout failed",
          description: "Unable to start checkout process. Please try again.",
          variant: "destructive",
        })
        setPurchaseLoading(null)
      }
    }
  }

  if (loading) {
    return (
        <div className="container px-4 py-8 mx-auto md:px-6 md:py-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Pricing & Credits</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Loading subscription plans...</p>
          </div>
        </div>
    )
  }

  return (
      <div className="container px-4 py-8 mx-auto md:px-6 md:py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Pricing & Credits</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose the plan that works best for your interior design needs
          </p>
        </div>

        {hasAnnualPlans && (
            <div className="flex justify-center mb-12">
              <PricingToggle billingPeriod={billingPeriod} onToggle={setBillingPeriod} />
            </div>
        )}

        <div
            className={
              plans.length < 3 ? "flex flex-wrap justify-center gap-8 mb-16" : "grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
            }
        >
          {plans.map((plan) => (
              <Card
                  key={plan.id}
                  className={`relative ${plan.popular ? "ring-2 ring-[#40A1AB] scale-105" : ""} ${
                      plans.length < 3 ? "w-80" : ""
                  }`}
              >
                {plan.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-[#40A1AB] text-white px-4 py-1">
                        <Star className="h-3 w-3 mr-1" />
                        Most Popular
                      </Badge>
                    </div>
                )}

                <CardHeader className="text-center">
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold">{plan.id === "business" ? "TBD" : `$${plan.price}`}</span>
                      {plan.price > 0 && <span className="text-gray-500 ml-1">/month</span>}
                    </div>
                    {plan.originalPrice && billingPeriod === "annual" && (
                        <div className="text-sm text-gray-500 mt-1">
                          <span className="line-through">${plan.originalPrice}/month</span>
                          <span className="text-green-600 ml-2">Save 20%</span>
                        </div>
                    )}
                  </div>
                  <CardDescription className="mt-2">{plan.description}</CardDescription>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-center">
                          <Check className="h-4 w-4 text-[#40A1AB] mr-3 flex-shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter>
                  {plan.id === "pro" ? (
                      isLoggedIn ? (
                          subscriptionLoading ? (
                              <Button className="w-full bg-[#40A1AB] hover:bg-[#3A919A] text-white" disabled>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Loading...
                              </Button>
                          ) : hasMonthlySubscription ? (
                              <Button className="w-full bg-[#40A1AB] hover:bg-[#3A919A] text-white" disabled>
                                Currently Subscribed
                              </Button>
                          ) : (
                              <Button
                                  onClick={() => handlePlanClick(plan.id)}
                                  className="w-full bg-[#40A1AB] hover:bg-[#3A919A] text-white"
                                  disabled={purchaseLoading === plan.id}
                              >
                                {purchaseLoading === plan.id ? (
                                    <>
                                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                      Processing...
                                    </>
                                ) : (
                                    plan.buttonText
                                )}
                              </Button>
                          )
                      ) : null
                  ) : (
                      <Button
                          onClick={() => handlePlanClick(plan.id)}
                          className={`w-full ${plan.buttonVariant === "default" ? "bg-[#40A1AB] hover:bg-[#3A919A] text-white" : ""}`}
                          variant={plan.buttonVariant}
                          disabled={purchaseLoading === plan.id}
                      >
                        {purchaseLoading === plan.id ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Processing...
                            </>
                        ) : (
                            plan.buttonText
                        )}
                      </Button>
                  )}
                </CardFooter>
              </Card>
          ))}
        </div>

        {!isLoggedIn && (
            <div className="text-center mb-16">
              <p className="text-lg text-gray-700">
                Ready to get started?{" "}
                <Link href="/login?tab=create-account" className="text-[#40A1AB] hover:text-[#3A919A] font-medium">
                  Register Now!
                </Link>
              </p>
              <p className="text-sm text-gray-500 mt-2">You'll choose your plan after registration.</p>
            </div>
        )}

        <CreditPacksSection />

        <FAQSection />

        <BusinessWaitlistModal isOpen={showWaitlistModal} onClose={() => setShowWaitlistModal(false)} />
      </div>
  )
}

function PricingPageFallback() {
  return (
      <div className="container px-4 py-8 mx-auto md:px-6 md:py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Loading Pricing...</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">Preparing subscription options for you</p>
        </div>
      </div>
  )
}

export function PricingPage() {
  return (
      <Suspense fallback={<PricingPageFallback />}>
        <PricingPageContent />
      </Suspense>
  )
}
